import { Container } from './container';
import { Definition } from './definition';
import { Reference } from './reference';
import { InjectionToken } from '../../interfaces/components/dependency-injection/container.interface';
import { Newable } from '../../interfaces/newable.interface';

export class ContainerBuilder extends Container {
  private definitions = new Map<InjectionToken, Definition>();

  public override compile (): void {
    super.compile();

    for (const [ id, definition ] of this.definitions) {
      const bind = this.container.bind(id).to(definition.getClass());

      if (definition.getMethodCalls().length > 0) {
        bind.onActivation(((context, injectable) => {
          const instance: Newable = injectable as Newable;

          for (const call of definition.getMethodCalls()) {
            if (!(call.method in instance)) {
              throw new Error(`Method ${call.method} doesn't exist on object of type ${instance.constructor.name}`);
            }

            for (let i = 0; i < call.parameters.length; i++) {
              if (call.parameters[ i ] instanceof Reference) {
                call.parameters[ i ] = context.container.get((call.parameters[ i ] as Reference).getId());
              }
            }

            instance[ call.method ].apply(instance, call.parameters);
          }

          return instance;
        }));
      }

      if (definition.getTags().length > 0) {
        for (const tag of definition.getTags()) {
          this.container.bind(tag).toService(id);
        }
      }
    }
  }

  public override set<T> (id: InjectionToken<T>, service: Newable<T> | object): void {
    if (this.isCompiled()) {
      throw new Error('Container is already compiled');
    }

    return super.set(id, service);
  }

  public register<T> (id: InjectionToken<T>, cls: Newable<T>): Definition<T> {
    return this.setDefinition(id, new Definition<T>(cls));
  }

  public getDefinitions (): Definition[] {
    return Object.fromEntries(this.definitions.entries());
  }

  public addDefinitions (definitions: { id: InjectionToken, definition: Definition }[]): void {
    for (const properties of definitions) {
      this.setDefinition(properties.id, properties.definition);
    }
  }

  public setDefinition<T> (id: InjectionToken<T>, definition: Definition<T>): Definition<T> {
    if (this.isCompiled()) {
      throw new Error('Adding definition to a compiled container is not allowed.');
    }

    this.definitions.set(id, definition);

    return definition;
  }

  public hasDefinition<T> (id: InjectionToken<T>): boolean {
    return this.definitions.has(id);
  }

  public getDefinition<T> (id: InjectionToken<T>): Definition<T> {
    if (!this.hasDefinition(id)) {
      throw new Error(`You have requested a non-existent service ${id.toString()}`);
    }

    return this.definitions.get(id) as Definition<T>;
  }
}
